import React from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, CalendarDays, Users, Eye } from "lucide-react";

function CampaignCard({ campaign, actions, showSteamAppId = true, linkTo }) {
  const navigate = useNavigate();

  function handleClick() {
    if (linkTo) navigate(linkTo);
  }

  return (
    <div
      onClick={handleClick}
      className={`rounded-2xl overflow-hidden transition-all ${linkTo ? "cursor-pointer" : ""} ${
        campaign.campaignType === "purchase"
          ? "bg-orange-950/20 border border-orange-500/10 hover:border-orange-500/25"
          : "bg-purple-950/20 border border-purple-500/10 hover:border-purple-500/25"
      }`}
    >
      <div className="flex flex-col sm:flex-row">
        {campaign.coverImageUrl && (
          <div className="flex items-center justify-center">
            <div className="sm:w-44 h-44 sm:h-44 flex-shrink-0 p-2">
              <div className="w-full h-full rounded-2xl overflow-hidden">
                <img
                  src={campaign.coverImageUrl}
                  alt={campaign.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold">{campaign.title}</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {showSteamAppId && <>Steam App ID: {campaign.steamAppId}</>}
                {(campaign.companyName || campaign.publisherName) && (
                  <span className={showSteamAppId ? "ml-3 text-gray-600" : "text-gray-600"}>by {campaign.companyName || campaign.publisherName}</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  campaign.campaignType === "purchase"
                    ? "bg-orange-500/10 text-orange-400 border border-orange-500/20"
                    : "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                }`}
              >
                {campaign.campaignType === "purchase" ? "Purchase" : "Wishlist"}
              </span>
              <span
                className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                  campaign.status === "pending"
                    ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
                    : campaign.status === "rejected"
                    ? "bg-red-500/10 text-red-400 border border-red-500/20"
                    : campaign.status === "active" && new Date(campaign.startDate) > new Date()
                    ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                    : campaign.status === "active"
                    ? "bg-green-500/10 text-green-400 border border-green-500/20"
                    : "bg-gray-500/10 text-gray-400 border border-gray-500/20"
                }`}
              >
                {campaign.status === "active" && new Date(campaign.startDate) > new Date()
                  ? "Starting Soon"
                  : campaign.status}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-400 line-clamp-2 mb-3">
            {campaign.description}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-3">
            <span className="flex items-center gap-1.5">
              <CalendarDays className="w-4 h-4" />
              {campaign.startDate} — {campaign.endDate}
            </span>
            <span className="flex items-center gap-1.5">
              Budget: ${campaign.maxBudget?.toLocaleString()}
            </span>
            <span className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              {campaign.joinedInfluencers || 0}/{campaign.maxInfluencers || "∞"}{" "}
              influencers
            </span>
            {campaign.minAudienceSize > 0 && (
              <span className="flex items-center gap-1.5">
                <Eye className="w-4 h-4" />
                {campaign.minAudienceSize.toLocaleString()}+ followers
              </span>
            )}
          </div>
          {campaign.platforms && campaign.platforms.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {campaign.platforms.map((p) => (
                <span
                  key={p}
                  className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400 capitalize"
                >
                  {p === "twitter" ? "X / Twitter" : p}
                </span>
              ))}
            </div>
          )}
          {campaign.genres && campaign.genres.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {campaign.genres.map((g) => (
                <span
                  key={g}
                  className="text-xs px-2 py-0.5 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20"
                >
                  {g}
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end justify-between">
            <div>{actions && <div className="flex flex-col gap-2">{actions}</div>}</div>
            <div className="flex items-center gap-1.5 text-lg font-bold text-white">
              <DollarSign className="w-5 h-5" />
              {campaign.useCountryPricing && campaign.pricingTiers && campaign.pricingTiers.length > 0
                ? `${Math.min(...campaign.pricingTiers.map(t => t.price))}–$${Math.max(...campaign.pricingTiers.map(t => t.price))}`
                : `${campaign.campaignType === "purchase" ? campaign.pricePerPurchase : campaign.pricePerWishlist}`
              }
              <span className="text-xs font-normal text-gray-500">/{campaign.campaignType === "purchase" ? "purchase" : "wishlist"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignCard;
